/**
 * CRM Client AI Chat - Ask questions about your business.
 * Available for Standard (10/mo) and Premium (unlimited) tier clients.
 */

import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { crmPortalService } from '@/services/api/crm-portal';
import { crmAuth } from '@/services/auth/crm-auth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Send, Loader2, Sparkles, Lock, Bot, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export function CRMAIChatPage() {
  const client = crmAuth.getClient();
  const queryClient = useQueryClient();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const tier = client?.subscription_tier || 'free';
  const hasAccess = tier === 'standard' || tier === 'premium';

  const { data: quota } = useQuery({
    queryKey: ['crm-ai-quota'],
    queryFn: () => crmPortalService.getAIQuota(),
    enabled: hasAccess,
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const chatMutation = useMutation({
    mutationFn: (message: string) => crmPortalService.sendAIChat(message),
    onSuccess: (data) => {
      setMessages(prev => [...prev, {
        id: `ai-${Date.now()}`,
        role: 'assistant',
        content: data.message || data.answer || 'I couldn\'t generate a response. Please try again.',
        timestamp: new Date(),
      }]);
      queryClient.invalidateQueries({ queryKey: ['crm-ai-quota'] });
    },
    onError: (err: any) => {
      const isQuota = err.response?.status === 429;
      setMessages(prev => [...prev, {
        id: `err-${Date.now()}`,
        role: 'assistant',
        content: isQuota
          ? 'You have reached your AI query limit for this month. Upgrade to Premium for unlimited queries.'
          : 'Sorry, something went wrong. Please try again.',
        timestamp: new Date(),
      }]);
    },
  });

  const handleSend = () => {
    if (!input.trim() || chatMutation.isPending) return;
    const text = input.trim();
    setMessages(prev => [...prev, { id: `user-${Date.now()}`, role: 'user', content: text, timestamp: new Date() }]);
    setInput('');
    chatMutation.mutate(text);
  };

  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <Sparkles className="h-12 w-12 text-[#5048e5]/30 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">AI Business Assistant</h2>
        <p className="text-sm text-muted-foreground mb-2">
          Ask questions about your business in plain English and get instant AI-powered answers.
        </p>
        <div className="space-y-2 text-xs text-muted-foreground mb-6 max-w-sm mx-auto text-left">
          <p>"What was my revenue last quarter?"</p>
          <p>"How much GST did I claim this year?"</p>
          <p>"Am I on track compared to last year?"</p>
          <p>"What deductions might I be missing?"</p>
        </div>
        <Button onClick={() => window.location.href = '/crm/subscription'} className="bg-[#5048e5] hover:bg-[#443fcc]">
          <Lock className="h-4 w-4 mr-2" /> Upgrade to Standard
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] max-w-3xl mx-auto">
      {/* Header */}
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#5048e5]" /> AI Business Assistant
          </h1>
          <p className="text-xs text-muted-foreground">Ask anything about your business</p>
        </div>
        {quota && (
          <Badge variant="outline" className="text-xs">
            {quota.used}/{quota.max === -1 ? '\u221E' : quota.max} queries
          </Badge>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 [&::-webkit-scrollbar]:hidden">
        {messages.length === 0 && (
          <div className="text-center py-12">
            <Sparkles className="h-8 w-8 text-[#5048e5]/20 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground mb-4">Ask me anything about your business</p>
            <div className="flex flex-wrap justify-center gap-2">
              {['What was my revenue this year?', 'How are my expenses trending?', 'When is my next BAS due?', 'What tax deductions can I claim?'].map(q => (
                <button
                  key={q}
                  onClick={() => { setInput(q); }}
                  className="px-3 py-1.5 rounded-full border text-xs hover:bg-muted transition"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map(msg => (
          <div key={msg.id} className={cn('flex gap-3', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
            {msg.role === 'assistant' && (
              <div className="h-7 w-7 rounded-full bg-[#5048e5]/10 flex items-center justify-center shrink-0 mt-1">
                <Bot className="h-4 w-4 text-[#5048e5]" />
              </div>
            )}
            <div className={cn(
              'max-w-[80%] rounded-2xl px-4 py-3 text-sm',
              msg.role === 'user'
                ? 'bg-[#5048e5] text-white rounded-br-md'
                : 'bg-muted rounded-bl-md'
            )}>
              {msg.role === 'assistant' ? (
                <div className="prose prose-sm dark:prose-invert max-w-none [&_p]:mb-2 [&_ul]:mb-2 [&_table]:text-xs">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                </div>
              ) : (
                <p className="whitespace-pre-wrap">{msg.content}</p>
              )}
            </div>
            {msg.role === 'user' && (
              <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center shrink-0 mt-1">
                <User className="h-4 w-4" />
              </div>
            )}
          </div>
        ))}

        {chatMutation.isPending && (
          <div className="flex gap-3">
            <div className="h-7 w-7 rounded-full bg-[#5048e5]/10 flex items-center justify-center shrink-0">
              <Bot className="h-4 w-4 text-[#5048e5]" />
            </div>
            <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t p-3">
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder="Ask about your business..."
            className="flex-1 resize-none rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5048e5]/30"
            rows={2}
          />
          <Button onClick={handleSend} disabled={!input.trim() || chatMutation.isPending} className="bg-[#5048e5] hover:bg-[#443fcc] h-auto">
            {chatMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
